import { useCallback } from "react";

import type { FormValidationErrors } from "~/shared/hooks/use-form-validation.ts";
import { useFormValidation } from "~/shared/hooks/use-form-validation.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";
import { isPastShipDate } from "~/shared/utils/date-helpers.ts";

import type { SelectedAddressBookItems } from "../context/quote-address-context.tsx";

function hasCompletePhone(value: string | null | undefined): boolean {
	const digits = (value ?? "").replaceAll(/\D/g, "");
	return digits.length === 10;
}

function isMissing(value: string | null | undefined): boolean {
	return !value?.trim();
}

function validatePickupFields(
	fields: Set<string>,
	pickup: SelectedAddressBookItems["pickup"],
	shipmentDate: string | undefined,
	isInternal: boolean,
): void {
	const detail = pickup?.details.find((d) => d.stopType === 1);
	if (isMissing(pickup?.companyName)) fields.add("pickupName");
	if ((pickup?.companyName?.length ?? 0) > 50) fields.add("pickupNameLength");
	if (isMissing(shipmentDate)) fields.add("pickupShipmentDate");
	if (isPastShipDate(shipmentDate, isInternal))
		fields.add("pickupShipmentDatePast");
	if (isMissing(pickup?.address1)) fields.add("pickupAddress1");
	if ((pickup?.address1?.length ?? 0) > 50) fields.add("pickupAddress1Length");
	if ((pickup?.address2?.length ?? 0) > 50) fields.add("pickupAddress2Length");
	if (isMissing(detail?.hoursOpen)) fields.add("pickupHoursFrom");
	if (isMissing(detail?.hoursClose)) fields.add("pickupHoursTo");
	if ((detail?.contactName?.length ?? 0) > 50)
		fields.add("pickupContactNameLength");
}

function validateDeliveryFields(
	fields: Set<string>,
	delivery: SelectedAddressBookItems["delivery"],
): void {
	const detail = delivery?.details.find((d) => d.stopType === 2);
	if (isMissing(delivery?.companyName)) fields.add("deliveryName");
	if ((delivery?.companyName?.length ?? 0) > 50)
		fields.add("deliveryNameLength");
	if (isMissing(delivery?.address1)) fields.add("deliveryAddress1");
	if ((delivery?.address1?.length ?? 0) > 50)
		fields.add("deliveryAddress1Length");
	if ((delivery?.address2?.length ?? 0) > 50)
		fields.add("deliveryAddress2Length");
	if (!hasCompletePhone(detail?.contactPhone))
		fields.add("deliveryContactPhone");
	if (isMissing(detail?.hoursOpen)) fields.add("deliveryHoursFrom");
	if (isMissing(detail?.hoursClose)) fields.add("deliveryHoursTo");
	if ((detail?.contactName?.length ?? 0) > 50)
		fields.add("deliveryContactNameLength");
}

function validateItem(
	item: QuoteStateType["items"][number],
	isSortAndSegregate: boolean,
): Set<string> {
	const invalidFields = new Set<string>();
	if (isMissing(item.description)) invalidFields.add("description");
	if (
		(isSortAndSegregate || item.isHazmat) &&
		(item.pieceCount == null || item.pieceCount <= 0)
	)
		invalidFields.add("pieceCount");
	if (item.isHazmat) {
		if (isMissing(item.nmfc)) invalidFields.add("nmfc");
		if (isMissing(item.unNumber)) invalidFields.add("unNumber");
		if (isMissing(item.properShippingName))
			invalidFields.add("properShippingName");
		if (!item.hazmatClass?.id) invalidFields.add("hazmatClass");
		if (!item.packingGroupId?.id) invalidFields.add("packingGroupId");
	}
	return invalidFields;
}

function validateShipmentState(
	quoteState: QuoteStateType,
	selectedAddressBookItems: SelectedAddressBookItems,
	isInternal: boolean,
): FormValidationErrors {
	const fields = new Set<string>();
	const items: Record<number, Set<string>> = {};

	validatePickupFields(
		fields,
		selectedAddressBookItems.pickup,
		quoteState.shipmentDate,
		isInternal,
	);
	validateDeliveryFields(fields, selectedAddressBookItems.delivery);

	const isSortAndSegregate = quoteState.deliveryServices.some(
		(service) => service.name === "Sort and Segregate",
	);

	let hasHazmat = false;
	for (const item of quoteState.items) {
		const invalidFields = validateItem(item, isSortAndSegregate);
		if (invalidFields.size > 0) items[item.id] = invalidFields;
		if (item.isHazmat) hasHazmat = true;
	}

	if (hasHazmat) {
		if (isMissing(quoteState.emergencyContactName))
			fields.add("emergencyContactName");
		if (!hasCompletePhone(quoteState.emergencyContactPhone))
			fields.add("emergencyContactPhone");
	}

	return { fields, items };
}

export const useShipmentValidation = () => {
	const validationFn = useCallback(
		(
			quoteState: QuoteStateType,
			selectedAddressBookItems: SelectedAddressBookItems,
			isInternal = false,
		) =>
			validateShipmentState(quoteState, selectedAddressBookItems, isInternal),
		[],
	);

	return useFormValidation(validationFn);
};
